import {Dispatch, SetStateAction, useEffect} from 'react';
import {Edge, OnEdgesChange, useEdgesState} from 'reactflow';

import {ProcedureRunDesignFlowchartEdgeData} from './ProcedureRunDesignFlowchartEdge';

export type ProcedureRunDesignFlowchartLayoutEdge = Edge<ProcedureRunDesignFlowchartEdgeData>;

export interface UseProcedureRunDesignFlowchartEdgesArgs {
  initialEdges: Array<ProcedureRunDesignFlowchartLayoutEdge>;
}

export interface UseProcedureRunDesignFlowchartEdgesResult {
  edges: Array<ProcedureRunDesignFlowchartLayoutEdge>;
  setEdges: Dispatch<SetStateAction<Array<ProcedureRunDesignFlowchartLayoutEdge>>>;
  onEdgesChange: OnEdgesChange;
}

/**
 * Reconciles the rendered edges with the incoming list. Incoming edges win for structural fields, but
 * carry over the existing edge's revealed visibility (`style`) so a re-build (e.g. when adding a
 * condition briefly resets the layout) doesn't re-hide an already-revealed flowchart.
 */
function mergeEdgesAndPreserveEdgeVisibility(
  currentEdges: Array<ProcedureRunDesignFlowchartLayoutEdge>,
  newEdges: Array<ProcedureRunDesignFlowchartLayoutEdge>
): Array<ProcedureRunDesignFlowchartLayoutEdge> {
  const existingById = new Map(currentEdges.map((edge) => [edge.id, edge]));

  return newEdges.map((newEdge) => {
    const existing = existingById.get(newEdge.id);
    return existing ? {...newEdge, style: existing.style} : newEdge;
  });
}

/**
 * Owns the ReactFlow edge state for the procedure run design flowchart and keeps it in sync as the
 * underlying edge specs change.
 */
export function useProcedureRunDesignFlowchartEdges({
  initialEdges,
}: UseProcedureRunDesignFlowchartEdgesArgs): UseProcedureRunDesignFlowchartEdgesResult {
  const [edges, setEdges, onEdgesChange] = useEdgesState<ProcedureRunDesignFlowchartEdgeData>(initialEdges);

  useEffect(() => {
    setEdges((currentEdges) => mergeEdgesAndPreserveEdgeVisibility(currentEdges, initialEdges));
  }, [initialEdges, setEdges]);

  return {edges, setEdges, onEdgesChange};
}
